/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';

export default function LibrariesExample() {
  return (
    <>
      <Meta
        title="Vimeo Libraries"
        description="Examples of using the officially supported Vimeo libraries."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/api/guides/start'}>Getting Started</Path.Link>
            <Path.Current>Library Usage</Path.Current>
          </Path>
        }
        label="Library Usage"
        nav={{ page: 'getting-started' }}
      >
        <Header>PHP Library Examples</Header>
        <Paragraph size="2">
          This page covers the most common use cases of the PHP library. Did we miss one?{' '}
          <Link variant="minimal" href="https://vimeo.com/help/contact">
            Let us know!
          </Link>
        </Paragraph>
        <Header size="2" id="request-method">
          Working with the <code>request</code> method
        </Header>
        <Paragraph size="2">
          The API library has a <code>request</code> method that takes three parameters (see{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>
          ). It returns an associative array containing all of the relevant request information. Any
          API request available in the{' '}
          <Link variant="minimal" href="/api/reference">
            API reference
          </Link>{' '}
          is available through the <code>request</code> method.
        </Paragraph>
        <GuideTable
          tableNumber={1}
          description={
            <span>
              Parameters of the <code>request</code> method
            </span>
          }
          headers={['Parameter', 'Data type', 'Description', 'Example']}
          rows={[
            ['url', 'String', 'The URL path.', <code>/me</code>],
            [
              'params',
              'String',
              'An object containing all of your parameters.',
              <code>{'{per_page": 5, "filter" : "featured"}'}</code>,
            ],
            ['method', 'String', 'The HTTP method.', <span>GET, POST, PATCH, PUT, or DELETE</span>],
          ]}
        />
        <Paragraph size="2">
          To fetch a list of videos from the{' '}
          <code>
            <Link variant="minimal" href="/api/reference/videos#get_videos">
              get_videos
            </Link>
          </code>{' '}
          endpoint, use the following:
        </Paragraph>
        <CodeBlock language="php">
          $response = $lib-{'>'}request('/me/videos', ['per_page' ={'>'} 2], 'GET');
        </CodeBlock>
        <Paragraph size="2">
          To update a video from the{' '}
          <code>
            <Link variant="minimal" href="/api/reference/videos#edit_video">
              edit_video
            </Link>
          </code>{' '}
          endpoint, use the following:
        </Paragraph>
        <CodeBlock language="php">
          $response = $lib-{'>'}request('/me/videos', ['name'' ={'>'} 'The New Name'], 'PATCH');
        </CodeBlock>
        <Header size="2" id="response-array">
          Working with the <code>response</code> array
        </Header>
        <Paragraph size="2">
          The <code>response</code> array contains three keys (see{' '}
          <Link variant="minimal" href="#table-2">
            Table 2
          </Link>
          ).
        </Paragraph>
        <GuideTable
          tableNumber={2}
          description={
            <span>
              Keys of the <code>response</code> array
            </span>
          }
          headers={['Parameter', 'Data type', 'Description']}
          rows={[
            [
              'body',
              'Array',
              'The parsed request body. All responses are JSON, so we parse this for you and give you the result.',
            ],
            [
              'status',
              'Number',
              'The HTTP status code of the response. This partially informs you about the success of your API request.',
            ],
            ['headers', 'Array', 'An associative array containing all of the response headers.'],
          ]}
        />
        <Paragraph size="2">
          To fetch the first two videos uploaded to the authenticated user's account, use the
          following:
        </Paragraph>
        <CodeBlock language="php">
          $response = $lib-{'>'}request('/me/videos', ['per_page' ={'>'} 2], 'GET'); $body =
          $response['body'];
        </CodeBlock>

        <Header size="2" id="uploading-videos">
          Working with uploads
        </Header>

        <Paragraph size="2">
          For a full review of the upload API, check out{' '}
          <Link href="/api/upload/videos" variant="minimal">
            Working with Video Uploads
          </Link>
          .
        </Paragraph>
        <Header size="3">Uploading videos</Header>
        <Paragraph size="2">
          To upload videos, you must call the <code>upload</code> method. Internally, this library
          executes a tus upload approach and sends a file to the server with the tus upload
          protocol. For more information about tus, see the{' '}
          <Link variant="minimal" href="https://tus.io">
            tus site
          </Link>
          .
        </Paragraph>
        <Paragraph size="2">
          The <code>upload</code> method accepts two parameters (see{' '}
          <Link variant="minimal" href="#table-3">
            Table 3
          </Link>
          ) and returns the URI of the new video.
        </Paragraph>

        <GuideTable
          tableNumber={3}
          description={
            <span>
              Parameters of the <code>upload</code> method
            </span>
          }
          headers={['Parameter', 'Data type', 'Description']}
          rows={[
            ['file', 'String', 'Full path to the upload file on the local system.'],
            [
              'params',
              'Array',
              <span>
                Parameters to send when creating a new video, such as <code>name</code> or{' '}
                <code>privacy restrictions</code>. See the{' '}
                <code>
                  <Link variant="minimal" href="/api/reference/videos#upload_video">
                    upload_video
                  </Link>
                </code>{' '}
                documentation for supported parameters.
              </span>,
            ],
          ]}
        />

        <Paragraph size="2">
          To upload the video file located at <code>/tmp/ada.mp4</code>, use the following:
        </Paragraph>
        <CodeBlock language="php">$response = $lib-{'>'}upload('/tmp/ada.mp4')</CodeBlock>

        <Paragraph size="2">
          To upload the video file located at <code>/tmp/ada.mp4</code>, with{' '}
          <code>
            <b>privacy.view</b>
          </code>{' '}
          set to <code>anybody</code> and{' '}
          <b>
            <code>name</code>
          </b>{' '}
          set to <code>Ada</code>, use the following:
        </Paragraph>
        <CodeBlock language="php">
          $response = $lib-{'>'}upload('/tmp/ada.mp4', [ 'name' ={'>'} 'Ada', 'privacy' ={'>'} [
          'view' ={'>'}
          'anybody' ] ])
        </CodeBlock>

        <Header size="3">Replacing videos from the server</Header>

        <Paragraph size="2">
          To replace the source file of a video, you must call the <code>replace</code> method. This
          method accepts two parameters (see{' '}
          <Link variant="minimal" href="#table-4">
            Table 4
          </Link>
          ) and returns the URI of the replaced video.
        </Paragraph>
        <GuideTable
          tableNumber={4}
          description={
            <span>
              Parameters of the <code>replace</code> method
            </span>
          }
          headers={['Parameter', 'Data type', 'Description']}
          rows={[
            [
              'video_uri',
              'String',
              'The URI of the original video. Once uploaded and successfully transcoded, your source video file is swapped with this new video file.',
            ],
            ['file', 'String', 'Full path to the upload file on the local system.'],
          ]}
        />

        <Paragraph size="2">
          To replace the source file of a video with URI <code>/videos/12345</code>, use the
          following:
        </Paragraph>
        <CodeBlock language="php">
          $response = $lib-{'>'}replace('/videos/12345', '/tmp/ada-v2.mp4')
        </CodeBlock>

        <Header size="3">Uploading or replacing videos from the client</Header>

        <Paragraph size="2">
          To upload from the client, you must mix some server-side and client-side API requests. We
          support two workflows, the first of which is much more straightforward than the second.
        </Paragraph>

        <ul>
          <li>
            <strong>Simple POST uploads</strong>. This workflow is well documented on our{' '}
            <Link variant="minimal" href="/api/upload/videos#simple-upload.">
              Working with Video Uploads
            </Link>{' '}
            guide.
          </li>
          <li>
            <strong>Streaming uploads</strong>. Streaming uploads support progress bars and
            resumable uploading. If you want to perform these uploads client-side, you need to start
            with some server-side requests. Read through the{' '}
            <Link variant="minimal" href="/api/upload/videos#resumable-upload">
              Vimeo documentation
            </Link>{' '}
            first. Step 1 should be performed on the server, while Steps 2 and 3 can be performed on
            the client. With this workflow, the video is never transferred to your servers.
          </li>
        </ul>

        <Header size="3">Uploading videos from a URL</Header>

        <Paragraph size="2">
          To upload videos from a public URL (also called <em>pull uploads</em>), use a single API
          call.
        </Paragraph>

        <CodeBlock language="php">
          $video_response = $lib-{'>'}request('/me/videos', [ 'upload' ={'>'} [ 'approach' ={'>'}{' '}
          'pull', 'link' ={'>'} $url ], ], 'POST');
        </CodeBlock>

        <Paragraph size="2">
          For more information on uploads, check out{' '}
          <Link variant="minimal" href="/api/reference/videos/#upload_video">
            Upload a video
          </Link>
          .
        </Paragraph>

        <Header size="3">Uploading images</Header>

        <Paragraph size="2">
          To upload an image, call the <code>uploadImage</code> method. For more information, check
          out the{' '}
          <Link
            variant="minimal"
            href="https://github.com/vimeo/vimeo.php/blob/master/example/upload_image.php"
          >
            <code>upload_image.php</code> example
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          The <code>uploadImage</code> method takes three parameters (see{' '}
          <Link variant="minimal" href="#table-5">
            Table 5
          </Link>
          ).
        </Paragraph>

        <GuideTable
          tableNumber={5}
          description={
            <span>
              Parameters for the <code>uploadImage</code> method
            </span>
          }
          headers={['Parameter', 'Data type', 'Required?', 'Description']}
          rows={[
            [
              'pictures_uri',
              'String',
              'Yes',
              <span>
                The URI to the pictures collection for a single resource, such as{' '}
                <code>/videos/12345/pictures</code>. You can always find this in the resource
                representation.
              </span>,
            ],
            ['file', 'String', 'Yes', 'The full path to the upload file on the local system.'],
            [
              'activate',
              'Boolean',
              'No',
              <span>
                If <code>true</code>, this picture becomes the default picture for the associated
                resource. The default value is <code>false</code>.
              </span>,
            ],
          ]}
        />

        <Paragraph size="2">
          To call the <code>uploadImage</code> method, use the following:
        </Paragraph>
        <CodeBlock language="php">
          $response = $lib-{'>'}uploadImage('/videos/12345/pictures',
          '/home/aaron/Downloads/ada.png', true)
        </CodeBlock>
      </GuideWrapper>
    </>
  );
}
